/* eslint-disable react/jsx-no-useless-fragment */

import {
  Button,
  Icon,
  notifyError,
  Tag,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  useMergeIntegration,
  useMergeLinkCreationMutation,
} from '@omnipresentgroup/domains';
import { hasSuperManagerScope } from '@omnipresentgroup/helpers';
import {
  MergeIntegrationStatus,
  MergeMagicLinkStatus,
} from '@omnipresentgroup/types';
import { useMemo, useState } from 'react';

import {
  IntegrationWidget,
  type IntegrationWidgetStatus,
} from '../../components';
import { useManagerUser } from '../../hooks';
import { WrappedMixpanel } from '../../mixpanelWrapper';
import {
  MergeCopyMagicLinkActionModal,
  MergeIntegrationModal,
  MergeWorkEmailsActionModal,
} from './components';

const INTEGRATION_TITLE = 'HRIS';
const ENABLE_INTEGRATION_TEXT = `Omnipresent now let's you sync time off data from your HRIS.`;
const HRIS = 'HRIS';
const START_INTEGRATION_BUTTON_LABEL = 'Start integration';
const ACTION_REQUIRED = 'Action required';
const ERROR_LINK_GENERATION_MESSAGE =
  'Failed to create integration link. Please try again later.';
const MERGE_INTEGRATION_CONNECT_BUTTON_LABEL = 'Connect now';

export const MergeIntegrationWidget = () => {
  const { modalProps } = useModal({});
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [copyMagicLinkModalOpen, setCopyMagicLinkModalOpen] = useState(false);
  const [missingWorkEmailsModalOpen, setMissingWorkEmailsModalOpen] =
    useState(false);

  const { authUser, userId, companyId } = useManagerUser();

  const { data: integrationStatus, isLoading: isIntegrationLoading } =
    useMergeIntegration();

  const { mutate: generateIntegrationLink, isPending: isLinkBeingGenerated } =
    useMergeLinkCreationMutation({
      onSuccess: () => {
        setConnectModalOpen(false);
        setCopyMagicLinkModalOpen(true);
      },
      onError: () => {
        notifyError({
          title: ERROR_LINK_GENERATION_MESSAGE,
        });
      },
    });

  const mixPanelProperties = useMemo(
    () => ({
      companyId,
      userId,
    }),
    [companyId, userId],
  );

  const isSuperManager = useMemo(
    () => authUser && hasSuperManagerScope(authUser),
    [authUser],
  );

  const isIntegrationLinkValid = useMemo(
    () =>
      !!integrationStatus?.magicLinkUrl &&
      integrationStatus.magicLinkUrl !== MergeMagicLinkStatus.EXPIRED &&
      integrationStatus.magicLinkUrl !== MergeMagicLinkStatus.REDACTED,
    [integrationStatus?.magicLinkUrl],
  );

  const isLinkGeneratedState = useMemo(
    () =>
      !!integrationStatus?.status &&
      (integrationStatus.status === MergeIntegrationStatus.LINK_GENERATED ||
        integrationStatus.status ===
          MergeIntegrationStatus.LINK_GENERATED_BUT_NOT_INTEGRATED) &&
      isIntegrationLinkValid,
    [integrationStatus?.status, isIntegrationLinkValid],
  );

  const isIntegrationComplete = useMemo(
    () => integrationStatus?.status === MergeIntegrationStatus.ACCOUNT_SYNCED,
    [integrationStatus?.status],
  );

  const missingWorkEmails = useMemo(
    () =>
      integrationStatus?.employees.filter(
        (employee) => !employee.workEmail || employee.workEmail.trim() === '',
      ).length || 0,
    [integrationStatus?.employees],
  );

  const mergeStatus: IntegrationWidgetStatus | JSX.Element = useMemo(() => {
    const copyMagicLinkNeeded = isLinkGeneratedState && isSuperManager;
    const fillInMissingWorkEmailsNeeded =
      missingWorkEmails > 0 && isSuperManager;

    const handleTagClick = () => {
      if (fillInMissingWorkEmailsNeeded) {
        setMissingWorkEmailsModalOpen(true);
      } else if (copyMagicLinkNeeded) {
        setCopyMagicLinkModalOpen(true);
      }
    };

    if (copyMagicLinkNeeded || fillInMissingWorkEmailsNeeded) {
      return (
        <Tag
          id="connection-status-tag-merge"
          intent="warning"
          onClick={handleTagClick}
        >
          {ACTION_REQUIRED}
        </Tag>
      );
    }

    if (isIntegrationComplete) {
      return 'connected';
    }

    const { status } = integrationStatus || {};

    if (
      status === MergeIntegrationStatus.AUTH_GRANTED ||
      status === MergeIntegrationStatus.TOKEN_GRANTED_BUT_NOT_INTEGRATED
    ) {
      return 'in_progress';
    }

    return 'not_connected';
  }, [
    integrationStatus,
    isIntegrationComplete,
    isLinkGeneratedState,
    isSuperManager,
    missingWorkEmails,
  ]);

  const handleLearnMoreClick = () => {
    WrappedMixpanel.track(
      'Merge Integration Read more clicked',
      mixPanelProperties,
    );
    setConnectModalOpen(true);
  };

  const getAction = () => {
    if (!isSuperManager) {
      return <></>;
    }

    if (!isIntegrationLinkValid && !isIntegrationComplete) {
      return (
        <Button
          loading={isLinkBeingGenerated}
          onClick={() => generateIntegrationLink()}
        >
          {START_INTEGRATION_BUTTON_LABEL}
        </Button>
      );
    }

    return <></>;
  };

  return (
    <>
      <IntegrationWidget
        id="merge"
        isLoading={isIntegrationLoading}
        key="merge-integration-widget"
      >
        <IntegrationWidget.Header
          title={
            (isIntegrationComplete && integrationStatus?.integrationName) ||
            INTEGRATION_TITLE
          }
          logo={
            (isIntegrationComplete && integrationStatus?.icon?.squareImage) || (
              <Icon icon="Link" size="16" />
            )
          }
          id="merge"
          status={mergeStatus}
        />
        <IntegrationWidget.Body
          content={ENABLE_INTEGRATION_TEXT}
          tags={[HRIS]}
        />
        <IntegrationWidget.Footer
          learnMoreHandle={handleLearnMoreClick}
          actionButtonLabel={
            !isIntegrationLinkValid && !isIntegrationComplete && isSuperManager
              ? MERGE_INTEGRATION_CONNECT_BUTTON_LABEL
              : undefined
          }
        />
      </IntegrationWidget>
      <MergeIntegrationModal
        modalProps={{
          ...modalProps,
          open: connectModalOpen,
          onOpenChange: setConnectModalOpen,
        }}
        handleClose={() => setConnectModalOpen(false)}
        canConfigure={isSuperManager}
        isConnected={isIntegrationComplete || false}
        action={getAction()}
      />
      {copyMagicLinkModalOpen &&
        isLinkGeneratedState &&
        integrationStatus?.magicLinkUrl && (
          <MergeCopyMagicLinkActionModal
            magicLink={integrationStatus.magicLinkUrl}
            modalProps={{
              ...modalProps,
              open: copyMagicLinkModalOpen,
              onOpenChange: setCopyMagicLinkModalOpen,
            }}
            handleClose={() => setCopyMagicLinkModalOpen(false)}
          />
        )}
      {missingWorkEmailsModalOpen && (
        <MergeWorkEmailsActionModal
          employees={integrationStatus?.employees || []}
          modalProps={{
            ...modalProps,
            open: missingWorkEmailsModalOpen,
            onOpenChange: setMissingWorkEmailsModalOpen,
          }}
          handleClose={() => setMissingWorkEmailsModalOpen(false)}
        />
      )}
    </>
  );
};
